import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { X, CreditCard, Calendar } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { apiRequest } from '@/shared/utils/queryClient';
import { useToast } from '@/shared/hooks/use-toast';

const paymentBookingSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Valid email is required'),
  phone: z.string().min(1, 'Phone number is required'),
  address: z.string().min(1, 'Address is required'),
  postcode: z.string().min(1, 'Postcode is required'),
  serviceType: z.enum(['inspection', 'one-time', 'quarterly', 'monthly']),
  description: z.string().optional(),
});

type PaymentBookingData = z.infer<typeof paymentBookingSchema>;

interface PaymentBookingFormProps {
  onClose: () => void;
}

export default function PaymentBookingForm({ onClose }: PaymentBookingFormProps) {
  const [location, navigate] = useLocation();
  const [estimatedCost, setEstimatedCost] = useState<number | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const { toast } = useToast();

  const form = useForm<PaymentBookingData>({
    resolver: zodResolver(paymentBookingSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      address: '',
      postcode: '',
      serviceType: 'inspection',
      description: '',
    },
  });

  const watchedServiceType = form.watch('serviceType');

  // Calculate cost when service type changes
  const calculateCost = async (serviceType: string) => {
    setIsCalculating(true);
    try {
      const response = await apiRequest('POST', '/api/calculate-cost', {
        propertyType: 'residential',
        propertySize: 'medium',
        pestTypes: ['general'],
        serviceType,
      });
      const data = await response.json();
      setEstimatedCost(parseFloat(data.estimatedCost));
    } catch (error) {
      console.error('Cost calculation failed:', error);
    } finally {
      setIsCalculating(false);
    }
  };

  // Calculate cost when service type changes
  useEffect(() => {
    if (watchedServiceType) {
      calculateCost(watchedServiceType);
    }
  }, [watchedServiceType]);

  const bookingMutation = useMutation({
    mutationFn: (data: PaymentBookingData) => apiRequest('POST', '/api/appointments', {
      ...data,
      appointmentDate: new Date(Date.now() + 24 * 60 * 60 * 1000), // Tomorrow
    }),
    onSuccess: (response) => {
      toast({
        title: 'Appointment created!',
        description: 'Redirecting to secure payment...',
      });
      onClose();
    },
    onError: () => {
      toast({
        title: 'Error creating appointment',
        description: 'Please try again or call us directly.',
        variant: 'destructive',
      });
    },
  });

  const handleBookAndPay = (data: PaymentBookingData) => {
    if (!estimatedCost) {
      toast({
        title: 'Cost calculation pending',
        description: 'Please wait for cost calculation to complete.',
        variant: 'destructive',
      });
      return;
    }

    // Navigate to checkout with payment information including phone for SMS
    const params = new URLSearchParams({
      amount: estimatedCost.toString(),
      customerName: data.name,
      customerEmail: data.email,
      customerPhone: data.phone,
      serviceType: data.serviceType,
      description: data.description || `${data.serviceType} service at ${data.address}`,
    });

    navigate(`/checkout?${params.toString()}`);
    onClose();
  };

  const handleBookLater = (data: PaymentBookingData) => {
    bookingMutation.mutate(data);
  };

  const serviceOptions = [
    { value: 'inspection', label: 'Pest Inspection', description: 'Comprehensive property assessment' },
    { value: 'one-time', label: 'One-Time Treatment', description: 'Single targeted treatment' },
    { value: 'quarterly', label: 'Quarterly Service', description: '4 visits per year' },
    { value: 'monthly', label: 'Monthly Service', description: 'Regular monthly maintenance' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Book Pest Control Service</h2>
          <button onClick={onClose}>
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        <form className="space-y-6">
          {/* Service Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Select Service</CardTitle>
              <CardDescription>Choose the type of pest control service you need</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                {serviceOptions.map((option) => (
                  <label key={option.value} className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      value={option.value}
                      {...form.register('serviceType')}
                      className="text-emerald-600 focus:ring-emerald-500"
                    />
                    <div className="flex-1">
                      <div className="font-medium">{option.label}</div>
                      <div className="text-sm text-gray-600">{option.description}</div>
                    </div>
                  </label>
                ))}
              </div>
              
              {/* Cost Display */}
              <div className="mt-4 p-4 bg-emerald-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Estimated Cost:</span>
                  <span className="text-xl font-bold text-emerald-600">
                    {isCalculating ? (
                      <div className="animate-spin w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full" />
                    ) : estimatedCost !== null ? (
                      `£${estimatedCost.toFixed(2)}`
                    ) : (
                      'Calculating...'
                    )}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">Final price may vary based on property assessment</p>
              </div>
            </CardContent>
          </Card>

          {/* Customer Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <input
                  {...form.register('name')}
                  type="text"
                  placeholder="Full Name"
                  className="border border-gray-300 rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none"
                />
                <input
                  {...form.register('email')}
                  type="email"
                  placeholder="Email Address"
                  className="border border-gray-300 rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <input
                  {...form.register('phone')}
                  type="tel"
                  placeholder="Phone Number"
                  className="border border-gray-300 rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none"
                />
                <input
                  {...form.register('postcode')}
                  type="text"
                  placeholder="Postcode"
                  className="border border-gray-300 rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none"
                />
              </div>

              <input
                {...form.register('address')}
                type="text"
                placeholder="Full Address"
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none"
              />

              <textarea
                {...form.register('description')}
                rows={3}
                placeholder="Describe your pest problem or any special requirements..."
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none"
              />
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              type="button"
              onClick={form.handleSubmit(handleBookAndPay)}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              size="lg"
              disabled={!estimatedCost || isCalculating}
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Book & Pay Now - £{estimatedCost?.toFixed(2) || '0.00'}
            </Button>
            
            <Button
              type="button"
              onClick={form.handleSubmit(handleBookLater)}
              variant="outline"
              size="lg"
              disabled={bookingMutation.isPending}
            >
              <Calendar className="w-4 h-4 mr-2" />
              Book Without Payment
            </Button>
          </div>

          <div className="text-center text-sm text-gray-500">
            <p>💳 Secure payment powered by Stripe</p>
            <p>📞 Questions? Call us at 07913024492</p>
          </div>
        </form>
      </div>
    </div>
  );
}